import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FinderDatesResponse, FinderFiltersResponse } from '@/services';
import React, { FC, useEffect, useState } from 'react';

import { Cinemas } from './cinemas';
import { Experiences } from './experiences';
import { FormattedMessage } from 'react-intl';

import { Movies } from './movies';
import { Searchable } from './search';
import { ShowTimes } from './show-times';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useMovieFinder } from '@/logic/state';
import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import ImageBoxFuture from '../shared/image-box-future';

const WarningIcon = '/icons/movie-finder/warning.svg';

interface FinderTabsProps {
  finderDates: FinderDatesResponse;
  finderFiltersResults: FinderFiltersResponse;
  isFiltersLoading?: boolean;
  itemToScroll: { type: Searchable; id: number };
  scrolledToMovie?: number;
  scrolledToCinema?: number;
  scrolledToExperience?: number;
  isError: boolean;
  setActiveTab?: (label: string) => void;
  tryAgain?: () => void;
}

enum TabsLabels {
  SHOWTIMES = 'movie.finder.show_times',
  MOVIES = 'movie.finder.movies',
  EXPERIENCES = 'movie.finder.experiences',
  CINEMAS = 'movie.finder.cinemas',
}

const tabsArray: {
  label: string;
  component: FC<Partial<FinderTabsProps>>;
}[] = [
  {
    label: TabsLabels.SHOWTIMES,
    component: ({ finderDates }) => <ShowTimes finderDates={finderDates} />,
  },
  {
    label: TabsLabels.MOVIES,
    component: ({ finderFiltersResults, isFiltersLoading, scrolledToMovie }) => (
      <Movies
        films={finderFiltersResults?.films}
        isLoading={isFiltersLoading}
        scrolledToMovie={scrolledToMovie}
      />
    ),
  },
  {
    label: TabsLabels.EXPERIENCES,
    component: ({ finderFiltersResults, scrolledToExperience }) => (
      <Experiences
        experiences={finderFiltersResults?.experiences}
        scrolledToExperience={scrolledToExperience}
      />
    ),
  },
  {
    label: TabsLabels.CINEMAS,
    component: ({ finderFiltersResults, scrolledToCinema }) => (
      <Cinemas
        cinemas={finderFiltersResults?.cinemas}
        scrolledToCinema={scrolledToCinema}
      />
    ),
  },
];

export const FinderTabs: FC<FinderTabsProps> = ({
  finderDates,
  finderFiltersResults,
  isFiltersLoading,
  itemToScroll,
  isError,
  setActiveTab,
  tryAgain,
}) => {
  const theme = useTheme();
  const [value, setValue] = useState<string>('0');
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('tablet'));

  const [
    selectedDate,
    selectedMovies,
    selectedTimeDuration,
    selectedCinemas,
    selectedExperiences,
  ] = useMovieFinder(
    s => [
      s.selectedDate,
      s.selectedMovies,
      s.selectedTimeDuration,
      s.selectedCinemas,
      s.selectedExperiences,
    ],
    shallow
  );

  useEffect(() => {
    if (itemToScroll) handleScroll(itemToScroll);
  }, [itemToScroll]);

  const [scrolledToMovie, setScrolledToMovie] = useState<number>(null);
  const [scrolledToCinema, setScrolledToCinema] = useState<number>(null);
  const [scrolledToExperience, setScrolledToExperience] = useState<number>(null);

  const handleScroll = ({ type, id }): void => {
    if (type === Searchable.FILM) {
      setScrolledToMovie(id);
    }
    if (type === Searchable.CINEMA) {
      setScrolledToCinema(id);
    }
    if (type === Searchable.EXPERIENCE) {
      setScrolledToExperience(id);
    }
  };

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: string,
    label?: string
  ): void => {
    const key: string = label || tabsArray[newValue].label;
    setActiveTab(key);
    const index: string = tabsArray.findIndex(item => item.label === key).toString();
    setValue(index);
  };

  useEffect(() => {
    if (selectedDate && selectedTimeDuration)
      handleChange(null, null, TabsLabels.SHOWTIMES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedTimeDuration]);

  useEffect(() => {
    if (selectedMovies.length > 0) {
      handleChange(null, null, TabsLabels.MOVIES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMovies]);

  useEffect(() => {
    if (selectedCinemas.length > 0) {
      handleChange(null, null, TabsLabels.CINEMAS);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCinemas]);
  useEffect(() => {
    if (selectedExperiences.length > 0) {
      handleChange(null, null, TabsLabels.EXPERIENCES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExperiences]);

  const a11yProps = (index: number) => {
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
    };
  };

  // useEffect(() => {
  //   const { filmIds, experienceIds, cinemaIds } = params;
  //   if (filmIds.length === 0 && experienceIds.length === 0 && cinemaIds.length === 0)
  //     handleChange(null, '0');
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [params]);

  const ErrorState = ({ tryAgain }: { tryAgain: () => void }): JSX.Element => {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <ImageBoxFuture
            img={WarningIcon}
            title={'warning-icon'}
            objectFit='contain'
            sx={{
              width: '24px',
              height: '24px',
              marginInlineEnd: '4px',
            }}
          />
          <Typography
            sx={{
              fontSize: {
                mobile: '18px',
                tablet: pxToRem(18),
              },
              lineHeight: '24px',
              fontWeight: '400',
              color: theme.palette.grey[50],
            }}
          >
            <FormattedMessage id='movie_finder.sorry.something.went.wrong' />
          </Typography>
        </Box>
        {!isFiltersLoading ? (
          <Typography
            onClick={() => tryAgain()}
            sx={{
              fontSize: {
                mobile: '18px',
                tablet: pxToRem(18),
              },
              lineHeight: '24px',
              fontWeight: '400',
              color: theme.palette.primary.main,
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
              marginTop: '26px',
            }}
          >
            <FormattedMessage id='global.try_again' />
          </Typography>
        ) : (
          <CustomizedCircularProgress size={30} thickness={4} />
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMediumScreen ? 'row' : 'column',
        marginTop: {
          mobile: '16px',
          tablet: pxToRem(60),
        },
        marginInline: {
          mobile: 0,
          tablet: pxToRem(128),
        },
      }}
    >
      <TabContext value={value.toString()}>
        <Tabs
          orientation={isMediumScreen ? 'vertical' : 'horizontal'}
          variant='scrollable'
          TabIndicatorProps={{ children: <span className='MuiTabs-indicatorSpan' /> }}
          value={value}
          onChange={handleChange}
          sx={{
            '& .MuiTabs-indicator': {
              display: 'flex',
              justifyContent: 'flex-start',
              backgroundColor: 'transparent',
              bottom: '12px',
              marginInlineStart: '16px',
            },
            '.MuiTabs-indicatorSpan': {
              maxWidth: 40,
              width: '100%',
              display: 'flex',
              backgroundColor: theme.palette.primary.main,
            },
            minWidth: {
              tablet: pxToRem(122),
            },
          }}
        >
          {tabsArray.map((tab, index: number) => (
            <Tab
              key={`tab-${tab.label}`}
              {...a11yProps(index)}
              value={index.toString()}
              sx={{
                whiteSpace: 'nowrap !important',
              }}
              label={
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      paddingBlock: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: {
                          mobile: '16px',
                          tablet: pxToRem(16),
                        },
                        lineHeight: '24px',
                        color:
                          Number(value) === tabsArray.findIndex(item => item === tab)
                            ? theme.palette.grey[90]
                            : theme.palette.grey[70],
                        fontFamily:
                          Number(value) === tabsArray.findIndex(item => item === tab)
                            ? theme.palette.typography.font.bold
                            : theme.palette.typography.font.regular,
                        fontWeight:
                          Number(value) === tabsArray.findIndex(item => item === tab)
                            ? 700
                            : 400,
                        textTransform: 'capitalize',
                      }}
                    >
                      <FormattedMessage id={tab.label} />
                    </Typography>
                  </Box>
                </Box>
              }
            />
          ))}
        </Tabs>

        <Box
          sx={{
            paddingTop: '20px',
            maxHeight: {
              mobile: '50vh',
              tablet: 'unset',
            },
            overflowY: 'auto',
            width: '100%',
          }}
        >
          <Box
            sx={{
              paddingBottom: {
                mobile: '60px',
                tablet: 0,
              },
            }}
          >
            {tabsArray.map(({ label, component: Component }, index: number) => (
              <TabPanel
                key={`tab-panel-${label}`}
                value={index.toString()}
                sx={{
                  padding: {
                    mobile: '16px',
                    tablet: pxToRem(48),
                  },
                  paddingTop: '0px !important',
                  width: '100%',
                }}
              >
                {!isError ? (
                  <Component
                    finderDates={finderDates}
                    finderFiltersResults={finderFiltersResults}
                    isFiltersLoading={isFiltersLoading}
                    scrolledToMovie={scrolledToMovie}
                    scrolledToCinema={scrolledToCinema}
                    scrolledToExperience={scrolledToExperience}
                  />
                ) : (
                  <ErrorState tryAgain={tryAgain} />
                )}
              </TabPanel>
            ))}
          </Box>
        </Box>
      </TabContext>
    </Box>
  );
};
